import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { IoCalendarOutline, IoChevronDown } from 'react-icons/io5';
import './chart-range-picker.css';

export function ChartRangePicker<T extends number>({ options, value, onApply, start, end }: {
  options: { value: T; label: string }[];
  value: T;
  start: string;
  end: string;
  onApply: (value: T, start: string, end: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const id = useId();
  const dialog = useRef<HTMLDialogElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value);
  const [draftStart, setDraftStart] = useState(start);
  const [draftEnd, setDraftEnd] = useState(end);
  const valid = draft !== -1 || (!!draftStart && !!draftEnd && draftStart <= draftEnd);
  const close = () => setOpen(false);
  useEffect(() => {
    if (!open || !dialog.current) return;
    const panel = dialog.current;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    panel.showModal();
    return () => {
      panel.close();
      document.body.style.overflow = previous;
      trigger.current?.focus({ preventScroll: true });
    };
  }, [open]);
  return <>
    <button ref={trigger} type="button" className="chart-range-trigger" aria-haspopup="dialog" aria-expanded={open}
      onClick={() => { setDraft(value); setDraftStart(start); setDraftEnd(end); setOpen(true); }}>
      <IoCalendarOutline aria-hidden="true" />
      <span>{options.find(option => option.value === value)?.label}</span>
      <IoChevronDown aria-hidden="true" />
    </button>
    {open && createPortal(<dialog ref={dialog} className="chart-range-sheet" dir={i18n.dir()}
      aria-labelledby={`${id}-title`} onCancel={close}
      onClick={event => {
        if (event.target !== event.currentTarget) return;
        const bounds = event.currentTarget.getBoundingClientRect();
        if (event.clientY < bounds.top || event.clientX < bounds.left || event.clientX > bounds.right) close();
      }}>
      <div className="chart-range-sheet__grab" aria-hidden="true" />
      <header className="chart-range-sheet__header">
        <button type="button" onClick={close}>{t('charts.rangePickerCancel')}</button>
        <h2 id={`${id}-title`}>{t('charts.rangePickerTitle')}</h2>
        <button type="button" disabled={!valid} onClick={() => { onApply(draft, draftStart, draftEnd); close(); }}>{t('charts.rangePickerApply')}</button>
      </header>
      <fieldset className="chart-range-sheet__options">
        <legend className="sr-only">{t('charts.rangePickerTitle')}</legend>
        {options.map(option => <label key={option.value} className="chart-range-sheet__option">
          <span>{option.label}</span>
          <input type="radio" name={`${id}-range`} value={option.value} checked={draft === option.value}
            onChange={() => setDraft(option.value)} />
        </label>)}
      </fieldset>
      {draft === -1 && <div className="chart-range-sheet__dates">
        <label>{t('charts.from')}<input type="date" value={draftStart} max={draftEnd || undefined}
          onInput={event => setDraftStart(event.currentTarget.value)} onChange={event => setDraftStart(event.target.value)} onBlur={event => setDraftStart(event.currentTarget.value)} /></label>
        <label>{t('charts.to')}<input type="date" value={draftEnd} min={draftStart || undefined}
          onInput={event => setDraftEnd(event.currentTarget.value)} onChange={event => setDraftEnd(event.target.value)} onBlur={event => setDraftEnd(event.currentTarget.value)} /></label>
        {!valid && <p role="status">{t('charts.pickBothDates')}</p>}
      </div>}
    </dialog>, document.body)}
  </>;
}
